/**
 * Hidden routes — shared, environment-agnostic definitions
 * -------------------------------------------------------
 *
 * This module holds the pure data + matching logic for the hidden-routes
 * feature. It intentionally does NOT touch `import.meta.env` so it can be
 * imported both by the runtime router (browser) and by the Vite build plugin
 * (`build/plugins/hidden-routes-warn.ts`) running under plain Node.
 *
 * See `src/router/routes/hidden-routes.config.ts` for the runtime wiring and
 * developer-facing documentation.
 */

/** Environment modes an entry can target. */
export type HiddenRouteEnv = 'local' | 'prod';

export interface HiddenRouteEntry {
  /**
   * elegant-router route names to hide. Matching a parent hides all of its
   * children as well.
   */
  routeNames?: string[];
  /**
   * `src/views/...` folder prefixes to hide (matches the view referenced by a
   * route's `component`).
   */
  viewPathPrefixes?: string[];
  /**
   * Which environments this entry is hidden in. Provide both `'local'` and
   * `'prod'` to hide during `pnpm run dev` as well as production builds.
   *
   * Defaults to `['prod']` (hidden in production builds only) when omitted.
   */
  environments?: HiddenRouteEnv[];
  /** Short human-readable reason, surfaced in the warnings. */
  reason?: string;
}

/**
 * The list of routes/views to hide.
 *
 * Add work-in-progress features here. Keep entries close to the view folder you
 * are developing so it is obvious what is gated.
 */
export const HIDDEN_ROUTES: HiddenRouteEntry[] = [
  {
    reason: 'Sponsorship feature is still under development',
    routeNames: ['sponsorship'],
    viewPathPrefixes: ['src/views/sponsorship'],
    // Hidden in production builds only; still reachable during local dev.
    environments: ['prod']
  },
  {
    reason: 'Invoices is still under development - not ready for production yet, DO NOT change.',
    routeNames: ['invoices'],
    viewPathPrefixes: ['src/views/billing/invoices'],
    // Hidden in production builds only; still reachable during local dev.
    environments: ['prod']
  }
  , {
    reason: 'model studio is still under development - not ready for production yet, DO NOT change.',
    routeNames: ['model-studio'],
    viewPathPrefixes: ['src/views/model-studio'],
    // Hidden in production builds only; still reachable during local dev.
    environments: ['prod']
  }
];

export const DEFAULT_ENVIRONMENTS: HiddenRouteEnv[] = ['prod'];

const VIEW_PREFIX = 'view.';
const FIRST_LEVEL_ROUTE_COMPONENT_SPLIT = '$';

/** Whether an entry is active (should hide) for the given environment. */
export function isEntryActiveForEnv(entry: HiddenRouteEntry, env: HiddenRouteEnv): boolean {
  const environments = entry.environments ?? DEFAULT_ENVIRONMENTS;
  return environments.includes(env);
}

/**
 * Convert an elegant-router `component` string into a `src/views/...` path.
 *
 * Examples:
 * - `view.sponsorship_apply` -> `src/views/sponsorship/apply`
 * - `layout.base$view.models` -> `src/views/models`
 * - `layout.base` -> `null` (no view component)
 */
export function componentToViewPath(component?: string): string | null {
  if (!component) return null;

  const viewToken = component.includes(FIRST_LEVEL_ROUTE_COMPONENT_SPLIT)
    ? component.split(FIRST_LEVEL_ROUTE_COMPONENT_SPLIT).find(token => token.startsWith(VIEW_PREFIX))
    : component;

  if (!viewToken || !viewToken.startsWith(VIEW_PREFIX)) return null;

  const viewName = viewToken.slice(VIEW_PREFIX.length);

  return `src/views/${viewName.replaceAll('_', '/')}`;
}

/** Flatten the active config into lookup structures for the given environment. */
export function buildLookups(entries: HiddenRouteEntry[], env: HiddenRouteEnv) {
  const activeEntries = entries.filter(entry => isEntryActiveForEnv(entry, env));

  const routeNames = new Set<string>();
  const viewPathPrefixes: string[] = [];

  activeEntries.forEach(entry => {
    entry.routeNames?.forEach(name => routeNames.add(name));
    entry.viewPathPrefixes?.forEach(prefix => viewPathPrefixes.push(prefix));
  });

  return { activeEntries, routeNames, viewPathPrefixes };
}
